import React, { useEffect, useRef } from "react";

/**
 * Cinematic reactive Orb — CSS-driven conic gradient sphere with:
 *  - Noise overlay for texture
 *  - Concentric expanding rings
 *  - Optional live audio-reactivity (Web Audio API) when `stream` is provided
 *  - Ambient breathing when idle
 *
 * Props:
 *  - active: boolean, is the orb "hot" (recording state visuals)
 *  - stream: optional MediaStream to drive scale from mic amplitude
 *  - size: number, px width
 */
export const Orb = ({ active = false, stream = null, size = 320, className = "" }) => {
  const wrapRef = useRef(null);
  const rafRef = useRef(0);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);

  // audio-reactive amplitude → CSS var --orb-scale
  useEffect(() => {
    if (!stream) return;
    let cancelled = false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (cancelled) return;
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        const avg = sum / data.length / 255; // 0..1
        const scale = 1 + Math.min(0.22, avg * 0.55);
        if (wrapRef.current) wrapRef.current.style.setProperty("--orb-scale", scale.toFixed(3));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (_) { /* ignore */ }
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      try { audioCtxRef.current?.close(); } catch (_) { /* ignore */ }
      audioCtxRef.current = null;
      analyserRef.current = null;
    };
  }, [stream]);

  // gentle idle breathing when no audio stream
  useEffect(() => {
    if (stream) return;
    let raf = 0;
    const start = performance.now();
    const loop = (t) => {
      const s = 1 + Math.sin((t - start) / 1400) * 0.03;
      if (wrapRef.current) wrapRef.current.style.setProperty("--orb-scale", s.toFixed(3));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [stream]);

  return (
    <div
      ref={wrapRef}
      className={`orb-wrap ${className}`}
      style={{ width: size, height: size }}
      data-active={active ? "true" : "false"}
      data-testid="hero-orb"
      aria-hidden
    >
      <div className="orb-halo" />
      <div className="orb-ring r1" />
      <div className="orb-ring r2" />
      <div className="orb-ring r3" />
      <div className="orb-ring r4" />
      <div className="orb-core" />
    </div>
  );
};

export default Orb;
